/**
 * Controller for lightboxes/modals using a wrapped a11y-dialog.
 *
 * There are two parts to this; the typical ElementController that is annotated
 * at each place where a lightbox is desired, and the skeleton class that
 * handles the dialog element itself. The latter is due to how a11y-dialog
 * (and modals in general) function, where the HTML for the modal should exist
 * in the DOM before it's used and thus should be a single instance (per type
 * at least) to avoid bloating the page. This is also true for many other
 * ARIA-driven things.
 *
 * The skeleton class is hidden in an IIFE to prevent multiple instantiations,
 * enforcing the single element pattern. This class handles everything inside
 * the modal (close and navigation buttons, keyboard events, element sizing...)
 * with the help of an a11y-dialog instance while being ignorant about how its
 * items got there.
 *
 * A requirement for the lightbox is that all other page content should be
 * wrapped in an element with `data-lightbox-sibling`. This will be hidden from
 * screen readers when the modal is open.
 *
 *     <div data-lightbox-sibling>
 *       All page content, including header and footer, excluding the modal.
 *     </div>
 *     <!--
 *     Use {% lightbox_skeleton %} when using django-common.
 *     See the lightbox_skeleton.html template.
 *     -->
 *     <div class="lightbox" aria-hidden="true" data-lightbox-skeleton>
 *       ...
 *     </div>
 *
 * The ElementController gets an instance for each group of items, where an
 * item is something that points to some kind of lightbox content. This can be
 * through a data attribute for an HTML modal or through image links for a
 * typical image gallery. Each item can be a trigger for itself, or have
 * multiple triggers. In short, an item is one 'slide' in the lightbox and a
 * trigger points to an item.
 *
 * The simplest version is image links with nothing explicit specified:
 *
 *     <div data-controller="Lightbox">
 *       <a href="large-dog.jpg">
 *         <img src="small-dog.jpg" alt="Dog">
 *       </a>
 *       <a href="large-cat.jpg" data-caption="Text about the cat">
 *         <img src="small-cat.jpg" alt="Cat">
 *       </a>
 *     </div>
 *
 * Each link to an image will become an item, with navigation between them
 * unless disabled. Use `data-caption` to display caption text below the image.
 * Multiple links to the same image will be deduped, i.e. all links will open
 * the modal but only the first will be used for the item content.
 *
 * To display HTML, use `data-lightbox-content` (here with previous/next
 * navigation disabled through `data-lightbox-has-nav`):
 *
 *     <div data-controller="Lightbox" data-lightbox-has-nav="false">
 *       <div>
 *         Some stuff
 *         <button
 *           type="button"
 *           data-lightbox-content="<p>More about the stuff</p>">
 *             Show more stuff
 *         </button>
 *       </div>
 *       <div>
 *         Some things
 *         <button
 *           type="button"
 *           data-lightbox-content="<p>These are our things</p>">
 *             Show more things
 *         </button>
 *       </div>
 *     </div>
 *
 * Multiple triggers for the same content can be achieved by moving the content
 * to some container and setting `data-lightbox-trigger` on children to that
 * container:
 *
 *     <div data-controller="Lightbox">
 *       <div data-lightbox-content="<p>More about the stuff</p>">
 *         <button type="button" data-lightbox-trigger>Show more stuff</button>
 *         Some long text about stuff
 *         <button type="button" data-lightbox-trigger>Show more stuff</button>
 *       </div>
 *       <div data-lightbox-content="<p>These are our things</p>">
 *         <button type="button" data-lightbox-trigger>Show more things</button>
 *         Some long text about things
 *         <button type="button" data-lightbox-trigger>Show more things</button>
 *       </div>
 *     </div>
 *
 * There are some options available via data attribute on the lightbox root
 * element for each instance:
 *
 * - `lightbox-has-nav`: If the previous/next navigation should be displayed
 *   when there are more than one item. Defaults to true.
 * - `lightbox-scrollable`: If modal content should be scrollable. Defaults to
 *   true for HTML and false for images.
 * - `lightbox-class`: Class name to set on the lightbox when opened.
 *
 * An example that isn't related to this implementation as such, but covers
 * the requirements for proper and accessible use:
 * https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/dialog.html
 */

import A11yDialog from 'a11y-dialog';

import ElementController from '../base-controllers/ElementController';
import EventManager from '../EventManager';
import modifierKeyWatcher from '../modifierKeyWatcher';
import {
  getData,
  selectFrom,
  selectSingle,
  selectSingleFrom,
  parseHTML,
  setInnerHTML,
} from '../utils/dom';
import {
  afterNextPaint,
  conditionalAssign,
  debounce,
  isBool,
  isNil,
  isNumber,
  isTruthy,
  not,
} from '../utils/js';

// Items, see docblock
const LIGHTBOX_CONTENT_DATA_ATTRIBUTE = 'lightbox-content';
const LIGHTBOX_TRIGGER_DATA_ATTRIBUTE = 'lightbox-trigger';
const LIGHTBOX_CAPTION_DATA_ATTRIBUTE = 'caption';

// Options
const OPTION_HAS_NAV_DATA_ATTRIBUTE = 'lightbox-has-nav';
const OPTION_SCROLLABLE_DATA_ATTRIBUTE = 'lightbox-scrollable';
const OPTION_CLASS_DATA_ATTRIBUTE = 'lightbox-class';

// ID set on the first heading in the modal, used for aria-labelledby
const LIGHTBOX_TITLE_ID = 'lightbox-title';

// Class name that is toggled on <html> when the modal is open
const LIGHTBOX_OPEN_CLASS_NAME = 'lightbox-open';

// Class names set on the lightbox when opened, depending on if the content
// should be contained within the viewport or not.
const LIGHTBOX_CONTAINED_CLASS_NAME = 'lightbox--contained';
const LIGHTBOX_SCROLLABLE_CLASS_NAME = 'lightbox--scrollable';

// CLass names for default image elements
const LIGHTBOX_FIGURE_CLASS_NAME = 'lightbox-figure';
const LIGHTBOX_CAPTION_CLASS_NAME = 'lightbox-caption';

// Screen reader labels and label templates
const GALLERY_LABEL_TEMPLATE_DATA_ATTRIBUTE = 'gallery-label-template';
const SINGLE_IMAGE_LABEL_DATA_ATTRIBUTE = 'single-image-label';
const CURRENT_POSITION_TEMPLATE_DATA_ATTRIBUTE = 'current-position-template';

// Internal
const IMAGE_HREF_REGEX = /\.(jpe?g|png|gif|webp)$/;
const CONTROLLERS_ID = 'lightbox';
const TYPE_IMAGE = 'image';
const TYPE_HTML = 'html';
const DEFAULT_OPTIONS = {
  hasNav: true,
  isScrollable: undefined,
  customClassName: '',
};

/**
 * Get the single instance of LightboxSkeleton, the class for managing the
 * a11y-dialog instance.
 */
const getLightboxSkeleton = (() => {
  class LightboxSkeleton extends EventManager {
    /**
     * Run initial modal setup and bind events.
     */
    constructor() {
      super();

      this._setInitialState();

      // Save elements
      this.root = selectSingle('[data-lightbox-skeleton]');
      this.body = selectSingleFrom(this.root, '[data-lightbox-body]');
      this.content = selectSingleFrom(this.root, '[data-lightbox-content]');
      this.closeButton = selectSingleFrom(this.root, '[data-lightbox-close]');
      this.nav = selectSingleFrom(this.root, '[data-lightbox-nav]');
      this.prevButton = selectSingleFrom(this.root, '[data-lightbox-prev]');
      this.nextButton = selectSingleFrom(this.root, '[data-lightbox-next]');
      this.prevPreload = selectSingleFrom(
        this.root,
        '[data-lightbox-prev-preload]',
      );
      this.nextPreload = selectSingleFrom(
        this.root,
        '[data-lightbox-next-preload]',
      );
      this.liveRegion = selectSingleFrom(this.root, '[data-lightbox-live]');

      // Get screen reader labels and label templates
      this.galleryLabelTemplate = getData(
        this.body,
        GALLERY_LABEL_TEMPLATE_DATA_ATTRIBUTE,
      );
      this.singleImageLabel = getData(
        this.body,
        SINGLE_IMAGE_LABEL_DATA_ATTRIBUTE,
      );
      this.currentPositionTemplate = getData(
        this.liveRegion,
        CURRENT_POSITION_TEMPLATE_DATA_ATTRIBUTE,
      );

      // Init a11y-dialog. Ensure a content container has been specified
      // correctly.
      const contentContainer = selectSingle('[data-lightbox-sibling]');
      if (
        !contentContainer ||
        !Array.from(contentContainer.parentNode.children).some(
          (el) => el === this.root,
        )
      ) {
        throw new Error(
          'The lightbox skeleton must have a sibling with `data-lightbox-sibling` that wraps all page content',
        );
      }
      // Create custom hide method to be able to stop it from closing if
      // controllers can't be destroyed
      const dialogHide = A11yDialog.prototype.hide;
      A11yDialog.prototype.hide = function LightboxA11yDialogHide(event) {
        if (!window.Controllers.destroy(CONTROLLERS_ID)) {
          if (event) {
            event.preventDefault();
          }
          return this;
        }
        return dialogHide.call(this, event);
      };
      this.modal = new A11yDialog(this.root, contentContainer);

      this._bindEvents();
    }

    /**
     * Set initial properties for the closed state.
     */
    _setInitialState() {
      this.items = [];
      this.currentIndex = 0;
      this.isOpen = false;
      this.type = null;
      this.hasNav = false;
      this.isScrollable = true;
      this.customClassNames = [];
      this.dataChanged = false;
    }

    /**
     * Bind all required event listeners.
     */
    _bindEvents() {
      // React to open/close
      this.modal.on('show', this._onModalOpen);
      this.modal.on('hide', this._onModalClose);

      // The overlay is covered by the body and can't be clicked, so it must
      // be handled manually
      this.on(this.body, 'click', this._handleModalBodyClick);

      // Handle prev/next buttons
      this.on(this.prevButton, 'click', this._handlePrevClick);
      this.on(this.nextButton, 'click', this._handleNextClick);

      // Handle arrow keys for prev/next
      this.on(this.body, 'keyup', this._handleModalBodyKeyup);

      // Keep track of changed state
      this.on(this.content, 'change', () => {
        this.dataChanged = true;
      });
    }

    /**
     * Set classes when the modal has been opened.
     *
     * Since this runs after the modal is shown, some things is better run in
     * the open() method.
     */
    _onModalOpen = () => {
      // Start listening for resize
      if (!this.isScrollable && this.type === TYPE_IMAGE) {
        this.on(window, 'resize', this._handleResize);
      }

      // Classes
      document.documentElement.classList.add(LIGHTBOX_OPEN_CLASS_NAME);
      if (this.isScrollable) {
        this.root.classList.add(LIGHTBOX_SCROLLABLE_CLASS_NAME);
        this.root.classList.remove(LIGHTBOX_CONTAINED_CLASS_NAME);
      } else {
        this.root.classList.add(LIGHTBOX_CONTAINED_CLASS_NAME);
        this.root.classList.remove(LIGHTBOX_SCROLLABLE_CLASS_NAME);
      }
    };

    /**
     * Remove the 'open' body class and reset state when the modal closes.
     */
    _onModalClose = () => {
      if (!this.isScrollable && this.type === TYPE_IMAGE) {
        this.off(window, 'resize', this._handleResize);
      }

      document.documentElement.classList.remove(LIGHTBOX_OPEN_CLASS_NAME);
      if (this.customClassNames.length) {
        this.root.classList.remove(...this.customClassNames);
      }

      this._setInitialState();
    };

    /**
     * Prevent keyup from triggering the close button right after opening.
     *
     * @see _preventKeyboardClose()
     * @param {Event} e - Keydown event.
     */
    _handleCloseKeyup = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
      }
      this._clearKeyboardClosePrevention();
    };

    /**
     * Remove the keyboard event listeners for the close button.
     *
     * @see _preventKeyboardClose()
     */
    _handleCloseKeydown = () => {
      this._clearKeyboardClosePrevention();
    };

    /**
     * Remove the keyboard event listeners for the close button.
     *
     * @see _preventKeyboardClose()
     */
    _clearKeyboardClosePrevention = () => {
      this.off(this.closeButton, 'keydown', this._handleCloseKeydown);
      this.off(this.closeButton, 'keyup', this._handleCloseKeyup);
    };

    /**
     * Prevent the initial keyup event from firing on the close button.
     *
     * This should be registered when the modal is opened with a keypress.
     *
     * When the modal is opened, the focus is set to the first focusable element
     * which can be the close button. If the modal is opened via keypress (e.g.
     * the space key on a trigger button) the release of the key will trigger
     * the click event (handled by a11y-dialog) on the newly focused close
     * button. Some very quick keypresses seem to not trigger this, but that
     * can likely vary between browsers and devices. To illustrate:
     *
     * Press space
     *   -> trigger.keydown
     *   -> modal open
     *   -> focus close button
     *   -> release key
     *   -> closeButton.keyup/click
     *   -> modal close
     *
     * Testing in Firefox, Chrome and Safari shows that the keyup event removes
     * itself properly on open, hopefully the same is true for other browsers.
     *
     * Pressing Enter seems to have its own behavior, triggering continuous
     * click events if held down. A fairly slow enter press doesn't trigger
     * the above problem though, so it's probably expected.
     */
    _preventKeyboardClose = () => {
      this.on(this.closeButton, 'keydown', this._handleCloseKeydown);
      this.on(this.closeButton, 'keyup', this._handleCloseKeyup);
    };

    /**
     * Close the modal if clicking in the modal body but outside the modal
     * content.
     *
     * @param {object} e - Click event.
     */
    _handleModalBodyClick = (e) => {
      // The event `target` will be the element that first got the click, so a
      // click inside any content will not result in the body or figure being
      // the target.
      if (
        e.target === this.body ||
        e.target.classList.contains(LIGHTBOX_FIGURE_CLASS_NAME)
      ) {
        this.close();
      }
    };

    /**
     * Navigate when pressing left/right arrow keys.
     *
     * @param {object} e - Keyup event.
     */
    _handleModalBodyKeyup = (e) => {
      if (this.hasNav) {
        if (e.key === 'ArrowLeft') {
          this.showPrevItem();
        } else if (e.key === 'ArrowRight') {
          this.showNextItem();
        }
      }
    };

    /**
     * Show previous item when clicking the previous button.
     */
    _handlePrevClick = () => {
      this.showPrevItem();
    };

    /**
     * Show next item when clicking the next button.
     */
    _handleNextClick = () => {
      this.showNextItem();
    };

    /**
     * Set the height limit on resize.
     */
    _handleResize = debounce(() => {
      this.limitHeight();
    }, 250);

    /**
     * Keep height within the viewport for default image view.
     */
    limitHeight = () => {
      const figure = selectSingleFrom(
        this.content,
        `.${LIGHTBOX_FIGURE_CLASS_NAME}`,
      );
      if (figure) {
        const figureStyle = window.getComputedStyle(figure);
        const figureOffset = [
          'marginTop',
          'marginBottom',
          'paddingTop',
          'paddingBottom',
        ].reduce((total, prop) => total + parseInt(figureStyle[prop], 10), 0);
        const img = selectSingleFrom(figure, 'img');
        const caption = selectSingleFrom(
          figure,
          `.${LIGHTBOX_CAPTION_CLASS_NAME}`,
        );
        const captionHeight = caption ? caption.offsetHeight : 0;
        // clientHeight on the HTML element is the viewport height when testing
        // and is supposed to be without scrollbars. Some comments indicate that
        // it can be the entire body height in some cases, so fall back to
        // innerHeight on the window which will be smaller then (though include
        // the height of a possible horizontal scrollbar). Also ensure the min
        // value isn't zero.
        const viewportHeight = Math.min.apply(
          null,
          [document.documentElement.clientHeight, window.innerHeight].filter(
            isTruthy,
          ),
        );
        const maxHeight = viewportHeight - captionHeight - figureOffset;
        img.style.maxHeight = `${maxHeight}px`;
      }
    };

    /**
     * Get the item at the specified index.
     *
     * @param {number} index - Item index.
     * @returns {Element}
     */
    _getItem(index) {
      if (!isNumber(index) || index < 0 || index > this.items.length - 1) {
        throw new RangeError(
          `Invalid index ${index}, expected 0–${this.items.length - 1}`,
        );
      }
      return this.items[index];
    }

    /**
     * Get the HTML content for the item at the specified index.
     *
     * @param {number} index - Item index.
     * @returns {string}
     */
    _getItemContent(index) {
      const item = this._getItem(index);
      let content = '';
      if (this.type === TYPE_HTML) {
        content = getData(item, LIGHTBOX_CONTENT_DATA_ATTRIBUTE);
      } else {
        const captionText = getData(item, LIGHTBOX_CAPTION_DATA_ATTRIBUTE);
        // Ensure the caption has text to show, not just empty HTML elements.
        const caption =
          captionText && parseHTML(captionText).textContent
            ? `<figcaption class="${LIGHTBOX_CAPTION_CLASS_NAME}">${captionText}</figcaption>`
            : '';
        content = `<figure class="${LIGHTBOX_FIGURE_CLASS_NAME}"><img src="${item.href}" alt="">${caption}</figure>`;
      }
      return content;
    }

    /**
     * Show the content for the specified item.
     *
     * @param {number} index - Item index.
     */
    showItem(index) {
      this.currentIndex = index;

      // Set content
      setInnerHTML(this.content, this._getItemContent(this.currentIndex));

      if (this.type === TYPE_HTML) {
        // Add id to heading for aria-labelledby
        const firstHeading = selectSingleFrom(this.content, 'h1, h2, h3');
        if (firstHeading) {
          firstHeading.id = LIGHTBOX_TITLE_ID;
        }
      } else if (this.type === TYPE_IMAGE) {
        // Let the content be inserted and rendered before limiting the height
        // since dimensions from the elements can't be read otherwise.
        afterNextPaint(this.limitHeight);
      }

      // Update the live region text
      this.liveRegion.textContent =
        this.items.length > 1
          ? this.currentPositionTemplate
              .replace(/\{count\}/, index + 1)
              .replace(/\{total\}/, this.items.length)
          : '';

      // Enable or disable prev/next buttons
      if (this.hasNav) {
        const hasPrev = index !== 0;
        const hasNext = index < this.items.length - 1;
        this.prevButton.disabled = !hasPrev;
        this.nextButton.disabled = !hasNext;
        if (hasPrev) {
          this.prevPreload.innerHTML = this._getItemContent(index - 1);
        }
        if (hasNext) {
          this.nextPreload.innerHTML = this._getItemContent(index + 1);
        }
      }
    }

    /**
     * Show the previous item if not at the start.
     */
    showPrevItem() {
      if (this.currentIndex > 0) {
        this.showItem(this.currentIndex - 1);
      }
    }

    /**
     * Show the next item if not at the end.
     */
    showNextItem() {
      if (this.currentIndex !== this.items.length - 1) {
        this.showItem(this.currentIndex + 1);
      }
    }

    /**
     * Open the modal with the specified set of items.
     *
     * @param {Array.<object>} items - All available items.
     * @param {number} [currentIndex] - The current item index, defaults to 0.
     * @param {object} [options] - Options.
     */
    open(items, currentIndex = 0, options = {}) {
      if (this.isOpen) {
        return;
      }

      this.items = items;
      const item = this._getItem(currentIndex);
      this.type = getData(item, LIGHTBOX_CONTENT_DATA_ATTRIBUTE)
        ? TYPE_HTML
        : TYPE_IMAGE;

      // Just using Object.assign will result in any null values from the
      // passed options overwriting defaults.
      const opts = conditionalAssign(not(isNil), DEFAULT_OPTIONS, options);
      this.hasNav = opts.hasNav && items.length > 1;
      this.isScrollable = isBool(opts.isScrollable)
        ? opts.isScrollable
        : this.type === TYPE_HTML;
      this.customClassNames = opts.customClassName.split(' ').filter(isTruthy);

      // Do ARIA labelling before settings any content and showing the modal,
      // to ensure it's already there for screen readers.
      if (this.type === TYPE_HTML) {
        this.body.removeAttribute('aria-label');
        this.body.setAttribute('aria-labelledby', LIGHTBOX_TITLE_ID);
      } else {
        this.body.removeAttribute('aria-labelledby');
        this.body.setAttribute(
          'aria-label',
          this.items.length > 1
            ? this.galleryLabelTemplate.replace(/\{count\}/, this.items.length)
            : this.singleImageLabel,
        );
      }

      // Set custom class names before opening to hopefully ensure no CSS
      // transitions trigger any flickering.
      if (this.customClassNames.length) {
        this.root.classList.add(...this.customClassNames);
      }

      this.showItem(currentIndex);
      this.modal.show();
      this.isOpen = true;

      this._preventKeyboardClose();

      if (!this.hasNav) {
        this.nav.setAttribute('hidden', '');
      } else {
        this.nav.removeAttribute('hidden');
      }

      window.Controllers.init(CONTROLLERS_ID, this.root);
    }

    /**
     * Close the modal if it's open.
     */
    close() {
      if (this.isOpen) {
        this.modal.hide();
      }
    }
  }

  let singleton;
  return () => {
    if (!singleton) {
      singleton = new LightboxSkeleton();
    }
    return singleton;
  };
})();

/**
 * Controller for each lightbox/modal group.
 *
 * @extends {ElementController}
 */
export default class Lightbox extends ElementController {
  static id = 'Lightbox';

  /**
   * DOM loaded, run initial modal setup and bind events.
   */
  init() {
    // Get lightbox instance
    this.modal = getLightboxSkeleton();

    this.setTriggersAndItems();
    this.buttonizeTriggers();
    this.bindEvents();
  }

  destroy() {
    if (!this.isScrollable && this.type === TYPE_IMAGE) {
      this.off(window, 'resize', this._handleResize);
    }
    this.modal.close();
    return true;
  }

  /**
   * Get and cache relevant elements.
   */
  setTriggersAndItems() {
    // Prioritize defined items, fall back to all image links.
    let items = selectFrom(
      this.getRoot(),
      `[data-${LIGHTBOX_CONTENT_DATA_ATTRIBUTE}]`,
    );
    if (!items.length) {
      items = selectFrom(this.getRoot(), 'a').filter((a) =>
        IMAGE_HREF_REGEX.test(a.href),
      );
      this.triggers = items;
    } else {
      // When not doing default images, handle the third docblock example with
      // nested triggers for one item.
      this.triggers = items.reduce((triggers, trigger) => {
        const childTriggers = selectFrom(
          trigger,
          `[data-${LIGHTBOX_TRIGGER_DATA_ATTRIBUTE}]`,
        );
        return childTriggers.length
          ? triggers.concat(childTriggers)
          : triggers.concat(trigger);
      }, []);
    }

    // Get unique items based on either the content that is to be inserted or
    // the image URL linked to.
    this.items = items.filter(
      (item, i) =>
        items.findIndex((otherItem) => this.isItemEqual(item, otherItem)) === i,
    );
  }

  /**
   * Make non-button modal triggers behave like buttons.
   */
  buttonizeTriggers() {
    this.triggers
      .filter(
        (trigger) =>
          !['button', 'input', 'a'].includes(trigger.nodeName.toLowerCase()),
      )
      .forEach((trigger) => {
        // Assigning to the DOM node
        /* eslint-disable no-param-reassign */

        trigger.setAttribute('role', 'button');
        // The tabIndex property defaults to -1 so it can't be used to check if
        // a tabindex is already set, must check the attribute.
        if (!trigger.hasAttribute('tabindex')) {
          trigger.tabIndex = 0;
        }
      });

    this.on(
      this.getRoot(),
      // Must be keydown rather than keyup to prevent a space press from
      // scrolling the page
      'keydown',
      '[role="button"]',
      this.handleButtonizedKeydown,
    );
  }

  /**
   * Bind event listeners.
   */
  bindEvents() {
    // Bind events for each group
    this.on(this.getRoot(), 'click', this.handleRootClick);
    this.on(this.getRoot(), 'keydown', this.handleRootKeydown);
  }

  /**
   * Check if two triggers represent the same lightbox content.
   *
   * @param {Element} triggerA - First trigger.
   * @param {Element} triggerB - Second trigger.
   * @returns {boolean}
   */
  isItemEqual(triggerA, triggerB) {
    const content = getData(triggerA, LIGHTBOX_CONTENT_DATA_ATTRIBUTE);
    if (content) {
      return getData(triggerB, LIGHTBOX_CONTENT_DATA_ATTRIBUTE) === content;
    }
    return triggerB.href === triggerA.href;
  }

  /**
   * Check if a trigger is activated with a 'new tab action'.
   *
   * @param {Element} trigger - Trigger that was clicked/activated.
   * @returns {boolean}
   */
  isOpeningNewTab = (trigger) =>
    // Buttons can't be opened in a new tab
    trigger.nodeName.toLowerCase() === 'a' &&
    modifierKeyWatcher.isHoldingNewTabKey();

  /**
   * Get the trigger closest to the specified element.
   *
   * @param {Element} el - Element to select from.
   * @returns {?Element}
   */
  getClosestTrigger(el) {
    // The triggers can be explicit or implicit, see setTriggersAndItems
    const explicit = el.closest(`[data-${LIGHTBOX_TRIGGER_DATA_ATTRIBUTE}]`);
    if (this.triggers.includes(explicit)) {
      return explicit;
    }
    const implicit = el.closest('a');
    if (this.triggers.includes(implicit)) {
      return implicit;
    }
    return null;
  }

  /**
   * Get the lightbox item for the specified trigger.
   *
   * @param {Element} trigger - Trigger element.
   * @returns {?Element}
   */
  getItemForTrigger(trigger) {
    // The items can be explicit or implicit, see setTriggersAndItems
    const explicit = trigger.closest(
      `[data-${LIGHTBOX_CONTENT_DATA_ATTRIBUTE}]`,
    );
    if (this.items.includes(explicit)) {
      return explicit;
    }
    const implicit = trigger.closest('a');
    if (this.items.includes(implicit)) {
      return implicit;
    }
    return null;
  }

  /**
   * Open the modal if an event is from a trigger.
   *
   * @param {Event} event - Trigger element event (click, keydown...).
   */
  maybeOpenModal(event) {
    // The target can be something like a span inside a trigger button, in
    // which case it should count.
    const trigger = this.getClosestTrigger(event.target);
    if (trigger && !this.isOpeningNewTab(trigger)) {
      event.preventDefault();
      // Since an item can have multiple triggers, the trigger itself may not
      // be an item.
      const item = this.getItemForTrigger(trigger);
      this.modal.open(this.items, this.items.indexOf(item), {
        hasNav: getData(this.getRoot(), OPTION_HAS_NAV_DATA_ATTRIBUTE),
        isScrollable: getData(this.getRoot(), OPTION_SCROLLABLE_DATA_ATTRIBUTE),
        customClassName: getData(this.getRoot(), OPTION_CLASS_DATA_ATTRIBUTE),
      });
    }
  }

  /**
   * Open the modal when clicking a trigger.
   *
   * @param {object} e - Click event.
   */
  handleRootClick = (e) => {
    this.maybeOpenModal(e);
  };

  /**
   * Open the modal if activating a trigger with the keyboard.
   *
   * @param {object} e - Keydown event.
   */
  handleRootKeydown = (e) => {
    // Buttons can be activated with enter or space.
    if (e.key === 'Enter' || e.key === ' ') {
      this.maybeOpenModal(e);
    }
  };

  /**
   * Open the modal if activating a buttonized trigger with the keyboard.
   *
   * @param {object} e - Keydown event.
   */
  handleButtonizedKeydown = (e) => {
    // Buttons can be activated with enter or space.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.maybeOpenModal(e);
    }
  };
}
